import { useQuery } from "react-query";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { OlapScoresQuery } from "src/context/OlapScores/useOlapScoresParams";
import { isQueryReady } from "src/api/axiosClient/utils";
import { PropertiesTree, ScorePerScoreId, ScorePerScoreIdSchema } from "./useGetOneActionPlanAction";
import z from "zod";
import { Post, PostSchema } from "../../post/postSchema";

export const getAllActionPlanActionsKey = "getAllActionPlanActions" as const;

type Options = {
    actionPlanID: string | undefined;
    scoresQuery?: OlapScoresQuery;
};
export const useGetAllActionPlanActions = (options: Options) => {
    const { actionPlanID, scoresQuery } = options;

    const scoresQueryKey = scoresQuery ? JSON.stringify(scoresQuery) : undefined;
    const isEnabled = actionPlanID !== undefined;

    const query = useQuery({
        queryKey: [getAllActionPlanActionsKey, actionPlanID, scoresQueryKey],
        staleTime: 5 * 60 * 1000,
        enabled: isEnabled,
        queryFn: async () => {
            const url = `/api/action_plans/${actionPlanID}/actions`;

            const response = await loggedInApiCall.get(url, {
                params: {
                    itemsPerPage: 20000,
                    ...scoresQuery,
                },
            });
            return response;
        },
        select: (resp) => {
            const rawData: unknown = resp.data["hydra:member"];

            const data =
                Array.isArray(rawData) ?
                    rawData
                        .filter((item) => {
                            if (item === null || item === undefined) return false;
                            if (typeof item !== "object") return false;
                            return (item as { action?: unknown }).action !== null;
                        })
                        .map((item) => {
                            const i = item as {
                                totalStart?: unknown;
                                totalEnd?: unknown;
                                totalImpact?: unknown;
                            };

                            return {
                                ...item,
                                totalStart: i.totalStart ?? [],
                                totalEnd: i.totalEnd ?? [],
                                totalImpact: i.totalImpact ?? [],
                            };
                        })
                :   [];

            const validatedData = zodParseWithLog(data, ActionPlanActionSchema.array(), getAllActionPlanActionsKey);
            return validatedData;
        },
    });

    const isReady = isQueryReady(query, isEnabled);
    return {
        ...query,
        isReady,
    };
};

type PostWithoutScores = Pick<Post, "id" | "campaign">;
const PostWithoutScoresSchema = PostSchema.pick({
    id: true,
    campaign: true,
});

export type ActionCsrTheme = {
    id: number;
    title: string;
    parent?: {
        id: number;
        title: string;
    };
};

const ActionCsrThemeSchema = z.object({
    id: z.number(),
    title: z.string(),
    parent: z
        .object({
            id: z.number(),
            title: z.string(),
        })
        .optional(),
});

export type ActionPlanActionList = {
    id: string;
    actionPlan: ActionPlan;
    actionId: number;
    action: {
        id: number;
        title: string;
        propertiesTree?: PropertiesTree;
        csrThemes: ActionCsrTheme[];
    };
    startSimulationSsqPost: PostWithoutScores;
    endSimulationSsqPost: PostWithoutScores;
    type?: string;
    userMessageTitle?: string;
    userMessageBody?: string;
    assignee?: string; // IRI string in getAllActionPlanActions
    order: number;

    totalStart: ScorePerScoreId | [];
    totalEnd: ScorePerScoreId | [];
    totalImpact: ScorePerScoreId | [];
};

const ActionPlanActionSchema: z.ZodType<ActionPlanActionList> = z.object({
    id: z.string(),
    actionPlan: z.string(),
    actionId: z.number(),
    action: z.object({
        id: z.number(),
        title: z.string(),
        propertiesTree: z.record(z.string(), z.number().array()).optional(),
        csrThemes: ActionCsrThemeSchema.array(),
    }),
    startSimulationSsqPost: PostWithoutScoresSchema,
    endSimulationSsqPost: PostWithoutScoresSchema,
    type: z.string().optional(),
    userMessageTitle: z.string().optional(),
    userMessageBody: z.string().optional(),
    assignee: z.string().optional(), // IRI string in getAllActionPlanActions
    order: z.number(),

    totalStart: ScorePerScoreIdSchema.or(z.tuple([])),
    totalEnd: ScorePerScoreIdSchema.or(z.tuple([])),
    totalImpact: ScorePerScoreIdSchema.or(z.tuple([])),
});

type ActionPlan = IRI;
